import {documentToReactComponents, Options} from '@contentful/rich-text-react-renderer'
import {Text as RichText} from '@contentful/rich-text-types'

import {Text, TextFontWeight, TextVariant} from '@/components/common/content/Text'
import {
  commonStyledOptions,
  inlineFallbackRenderNode,
  renderHyperlink,
  renderMark,
} from '@/components/contentful/styledOptions'
import {theme} from '@/theme/theme'
import {getHeadingElement} from '@/utils/contentful/headingLevels'
import {MOBILE_SCREENSIZE} from '@/utils/styles/breakpoints'

export const options: Options = {
  renderNode: {
    ['heading-2']: (node) => {
      const heading = (node.content[0] as RichText).value
      return (
        <>
          <div className="title">
            <Text variant={TextVariant.h3} element={getHeadingElement(node, 2)}>
              {heading}
            </Text>
          </div>
          <style jsx>{`
            .title {
              margin-top: ${theme.spacing[6]};
            }
            @media ${MOBILE_SCREENSIZE} {
              .title {
                margin-top: ${theme.spacing[5]};
              }
            }
          `}</style>
        </>
      )
    },
    ['heading-3']: (node) => {
      const heading = (node.content[0] as RichText).value
      return (
        <>
          <div className="title">
            <Text variant={TextVariant.h4} element={getHeadingElement(node, 3)}>
              {heading}
            </Text>
          </div>
          <style jsx>{`
            .title {
              margin-top: ${theme.spacing[6]};
            }
            @media ${MOBILE_SCREENSIZE} {
              .title {
                margin-top: ${theme.spacing[5]};
              }
            }
          `}</style>
        </>
      )
    },
    ['heading-4']: (node) => {
      const heading = (node.content[0] as RichText).value
      return (
        <>
          <div className="title">
            <Text
              variant={TextVariant.h5}
              element={getHeadingElement(node, 4)}
              fontWeight={TextFontWeight.bold}
            >
              {heading}
            </Text>
          </div>
          <style jsx>{`
            .title {
              margin-top: ${theme.spacing[5]};
            }
            @media ${MOBILE_SCREENSIZE} {
              .title {
                margin-top: ${theme.spacing[4]};
              }
            }
          `}</style>
        </>
      )
    },
    ['heading-5']: (node) => {
      const heading = (node.content[0] as RichText).value
      return (
        <>
          <div className="title">
            <Text
              variant={TextVariant.h6}
              element={getHeadingElement(node, 5)}
              fontWeight={TextFontWeight.bold}
            >
              {heading}
            </Text>
          </div>
          <style jsx>{`
            .title {
              margin-top: ${theme.spacing[4]};
            }
            @media ${MOBILE_SCREENSIZE} {
              .title {
                margin-top: ${theme.spacing[3]};
              }
            }
          `}</style>
        </>
      )
    },
    ['paragraph']: (node, children) => {
      return (
        <>
          <div className="paragraph">
            <Text>{children}</Text>
          </div>
          <style jsx>{`
            .paragraph {
              margin-top: ${theme.spacing[5]};
            }
            @media ${MOBILE_SCREENSIZE} {
              .paragraph {
                margin-top: ${theme.spacing[4]};
              }
            }
          `}</style>
        </>
      )
    },
    ['ordered-list']: (node, children) => (
      <>
        <ol className="list">{children}</ol>
        <style jsx>{`
          .list {
            margin-top: ${theme.spacing[4]};
            margin-left: ${theme.spacing[3]};
          }
          @media ${MOBILE_SCREENSIZE} {
            .list {
              margin-top: ${theme.spacing[3]};
              margin-left: ${theme.spacing[2]};
            }
          }
        `}</style>
      </>
    ),
    ['unordered-list']: (node, children) => (
      <>
        <ul className="list">{children}</ul>
        <style jsx>{`
          .list {
            margin-top: ${theme.spacing[4]};
            margin-left: ${theme.spacing[4]};
          }
          @media ${MOBILE_SCREENSIZE} {
            .list {
              margin-top: ${theme.spacing[3]};
              margin-left: ${theme.spacing[2]};
            }
          }
        `}</style>
      </>
    ),
    ['list-item']: (node) => {
      const UnTaggedChildren = documentToReactComponents(node as any, {
        renderMark,
        renderNode: {
          ...inlineFallbackRenderNode,
          ['list-item']: (node, children) => children,
          ['paragraph']: (node, children) => children,
          ['hyperlink']: renderHyperlink,
        },
      })

      return (
        <>
          <li className="item">
            <Text>{UnTaggedChildren}</Text>
          </li>
          <style jsx>{`
            .item {
              display: flex;
              margin-top: ${theme.spacing[3]};
            }
            .item::before {
              content: '';
              position: relative;
              top: 8px;
              left: 0;
              width: 7px;
              height: 7px;
              flex: 0 0 7px;
              margin-right: ${theme.spacing[3]};
              border-radius: 50%;
              background-color: ${theme.palette.primary.main};
            }
            @media ${MOBILE_SCREENSIZE} {
              .item {
                margin-top: ${theme.spacing[2]};
              }
            }
          `}</style>
        </>
      )
    },
    ...commonStyledOptions,
    ['hyperlink']: renderHyperlink,
  },
  renderMark,
}
